import {
   Badge,
   Box,
   Button,
   Flex,
   HStack,
   Image,
   Link,
   LinkBox,
   LinkOverlay,
   Select,
   Text,
} from '@chakra-ui/react';
import { useMediaQuery } from '@ifixit/ui/hooks/use-media-query';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { type Money, shouldShowProductRating } from '@ifixit/helpers';
import { useTranslations } from '@ifixit/i18n/hooks';
import { LocaleCode } from '@ifixit/i18n/types';
import { Rating } from '@ifixit/product-components';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
} from '@ifixit/shopify-storefront-client';
import { ProductVariantPrice } from '@ifixit/ui-chakra';
import 'Shared/cart_drawer';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { fireGlobal } from 'Shared/utils/global-events';
import { Dispatch, SetStateAction, useState, useEffect, type MouseEventHandler } from 'react';
import EmblaCarousel from '@ifixit/ui-chakra/embla-carousel/EmblaCarousel';
import { FaIcon } from '@ifixit/icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

export type Product = {
   image: string;
   url: string;
   title: string;
   reviews: {
      rating: number;
      count: number;
   };
   itemcode: string;
   handle: string;
   isDeviceProduct: boolean;
   initialVariant: Variant;
   variants: Variant[];
};

type Variant = {
   name: string;
   cartLineItem: CartLineItem;
   proPricesByTier: Record<string, Money>;
};

type ComponentTrackingString = 'Product Ad Cards - Answers' | 'Product Ad Cards - Guide';

type ProductCarouselProps = {
   products: Product[];
   shopifyStore: {
      country: CountryCode;
      currencyCode: string;
      language: LanguageCode;
      localeCode: string;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
   };
   deviceDisplayTitle: string;
   productListLink?: string;
   trackingString: ComponentTrackingString;
   invertBackground: boolean;
};

const PRODUCT_CARD_WIDTH = 230;

export function ProductAdCards({
   products,
   shopifyStore,
   deviceDisplayTitle,
   productListLink,
   trackingString,
   invertBackground,
}: ProductCarouselProps) {
   const t = useTranslations('ProductAdCards');

   // TODO: remove this once we have a proper 100vw overflow solution
   useEffect(() => {
      document.documentElement.style.setProperty('--overflow-x', 'clip');
      // clean up
      return () => void document.documentElement.style.removeProperty('--overflow-x');
   }, []);

   return (
      <Box
         position="relative"
         width="100vw"
         left="calc(50% - 50vw)"
         backgroundColor={invertBackground ? 'gray.100' : 'white'}
      >
         <ShopifyStorefrontProvider {...shopifyStore}>
            <Box
               maxWidth="1280px"
               mx="auto"
               px={{ base: 4, md: 5, lg: 8 }}
               py={invertBackground ? 4 : 0}
            >
               <Box gap={1.5}>
                  <Text fontSize={'3xl'} fontWeight="semibold">
                     {t('title')}
                  </Text>
                  {productListLink && (
                     <Link
                        href={productListLink}
                        fontSize="lg"
                        fontWeight="semibold"
                        color="brand.500"
                     >
                        {t('shopLink')}
                     </Link>
                  )}
               </Box>
               <ProductCarousel
                  products={products}
                  shopifyStore={shopifyStore}
                  deviceDisplayTitle={deviceDisplayTitle}
                  productListLink={productListLink}
                  trackingString={trackingString}
                  invertBackground={invertBackground}
               />
            </Box>
         </ShopifyStorefrontProvider>
      </Box>
   );
}

function ProductCarousel({
   products,
   shopifyStore,
   deviceDisplayTitle,
   productListLink,
   trackingString,
   invertBackground,
}: ProductCarouselProps) {
   const totalCardsWidth = PRODUCT_CARD_WIDTH * products.length;
   const fitsWithoutScroll = useMediaQuery(`(min-width: ${totalCardsWidth}px)`);
   const enableCarouselScroll = fitsWithoutScroll === false;
   const gray100 = '#f3f4f6'; // gray.100
   const gradientBackground = invertBackground ? gray100 : 'white';

   return (
      <Flex grow={0} position="relative" mx="auto" width="fit-content" maxWidth="100%">
         {enableCarouselScroll && (
            <Box
               position="absolute"
               pointerEvents="none"
               width="100%"
               height="100%"
               background={`linear-gradient(to right, ${gradientBackground}, transparent 25px, transparent calc(100% - 25px), ${gradientBackground} 100%)`}
               zIndex={'docked'}
            />
         )}
         <EmblaCarousel
            style={{
               paddingBlock: '16px',
               paddingInline: enableCarouselScroll ? '16px' : '',
               userSelect: 'none',
            }}
            gap={16}
            options={{
               active: enableCarouselScroll,
            }}
            slides={products.map((product, index) => (
               <ProductCard
                  key={index}
                  localeCode={shopifyStore.localeCode as LocaleCode}
                  product={product}
                  deviceDisplayTitle={deviceDisplayTitle}
                  productListLink={productListLink}
                  trackingString={trackingString}
                  invertBackground={invertBackground}
               />
            ))}
         />
      </Flex>
   );
}

type ProductCardProps = {
   localeCode: LocaleCode;
   product: Product;
   deviceDisplayTitle: string;
   productListLink?: string;
   trackingString: ComponentTrackingString;
   invertBackground: boolean;
};

export function ProductCard({
   localeCode,
   product,
   deviceDisplayTitle,
   productListLink,
   trackingString,
   invertBackground,
}: ProductCardProps) {
   const { image, url, title, isDeviceProduct, initialVariant, variants } = product;
   const onClick = trackProductCardClick(product, trackingString);
   const [selectedVariant, setSelectedVariant] = useState<Variant>(initialVariant);
   const t = useTranslations('ProductAdCards');
   return (
      <LinkBox
         borderColor="transparent"
         borderWidth="1px"
         borderRadius="lg"
         width={`${PRODUCT_CARD_WIDTH}px`}
         padding={3}
         display={'flex'}
         flexDirection="column"
         gap={4}
         transition={`border-color var(--chakra-transition-duration-normal)`}
         _hover={{ borderColor: 'brand.500' }}
         onClick={onClick}
         bgColor={invertBackground ? 'white' : 'gray.100'}
      >
         <Image
            p={3}
            width="100%"
            borderRadius="lg"
            outline="1px solid"
            outlineColor="gray.200"
            objectFit="cover"
            alt={title}
            src={image}
            bgColor="white"
         />
         <LinkOverlay
            as={Link}
            href={url}
            fontWeight="semibold"
            fontSize="lg"
            color="gray.900"
            _hover={{ textDecoration: 'none' }}
         >
            <Text noOfLines={2}>{title}</Text>
         </LinkOverlay>
         {shouldShowProductRating(product.reviews) && (
            <HStack>
               <Rating
                  value={product.reviews.rating}
                  size={5}
                  ariaLabel={t('rating', { rating: product.reviews.rating })}
               />
               <Text color="gray.600" fontSize="md" fontWeight="500" lineHeight="0">
                  {product.reviews.count}
               </Text>
            </HStack>
         )}
         <ProductVariantPrice
            price={selectedVariant.cartLineItem.price}
            compareAtPrice={selectedVariant.cartLineItem.compareAtPrice}
            proPricesByTier={selectedVariant.proPricesByTier}
            showDiscountLabel={false}
            size="medium"
            variant="subdued"
            localeCode={localeCode}
         />
         {isDeviceProduct && (
            <DeviceTitleCard device={deviceDisplayTitle} productListLink={productListLink} />
         )}
         <Flex direction="column" justify="end" width="100%" height="100%" gap={2}>
            {variants.length > 1 && (
               <VariantDropdown
                  variants={variants}
                  selectedVariant={selectedVariant}
                  setSelectedVariant={setSelectedVariant}
               />
            )}
            <BuyButton
               cartLineItem={selectedVariant.cartLineItem}
               trackingString={trackingString}
            />
         </Flex>
      </LinkBox>
   );
}

const DeviceTitleCard = ({
   device,
   productListLink,
}: { device: string; productListLink?: string }) => (
   <Badge
      as={Link}
      href={productListLink}
      borderRadius="lg"
      backgroundColor="green.200"
      color="green"
      size="base"
      width="fit-content"
      alignItems={'center'}
      gap={2}
      pr={2}
      whiteSpace={'wrap'}
      border="1px solid transparent"
      _hover={{ borderColor: 'green.400', textDecoration: 'none' }}
   >
      <FaIcon icon={faCircleCheck} color="green.600" boxSize="16px" />
      <Text noOfLines={2}>{device}</Text>
   </Badge>
);

const VariantDropdown = ({
   variants,
   selectedVariant,
   setSelectedVariant,
}: {
   variants: Variant[];
   selectedVariant: Variant;
   setSelectedVariant: Dispatch<SetStateAction<Variant>>;
}) => (
   <Select
      variant="outline"
      value={selectedVariant.cartLineItem.variantTitle}
      onChange={e => {
         const selectedVariant = variants.find(
            variant => variant.cartLineItem.variantTitle === e.target.value
         );
         if (!selectedVariant) {
            return;
         }
         setSelectedVariant(selectedVariant);
      }}
      overflow="hidden"
      textOverflow="ellipsis"
      backgroundColor="white"
      borderColor="gray.200"
      borderRadius="lg"
      cursor="pointer"
   >
      {variants.map(({ name, cartLineItem }) => (
         <option key={cartLineItem.variantTitle} value={cartLineItem.variantTitle}>
            {name}
         </option>
      ))}
   </Select>
);

type BuyButtonProps = {
   cartLineItem: CartLineItem;
   trackingString: ComponentTrackingString;
};
function BuyButton({ cartLineItem, trackingString }: BuyButtonProps) {
   const { addToCart, enabled: addToCartEnabled } = useAddToCart();
   const buyButtonText = _js('Add to cart');
   return (
      <Button
         isDisabled={!addToCartEnabled}
         onClick={() => {
            addToCart.mutate({
               analytics: { descriptor: trackingString },
               lines: [cartLineItem],
            });
            fireGlobal('cart.addcomplete');
         }}
         size="md"
         variant="cta"
         borderRadius="lg"
         cursor={addToCartEnabled ? 'pointer' : 'not-allowed'}
      >
         {buyButtonText}
      </Button>
   );
}

function trackProductCardClick(
   product: Product,
   trackingString: ComponentTrackingString
): MouseEventHandler {
   return event => {
      const element = event.target;
      if (element instanceof HTMLAnchorElement && element.href) {
         trackAnalyticsCustomEvent({
            eventCategory: trackingString,
            eventAction: `Product Card - ${product.itemcode} - ${product.title} - Click`,
            eventName: product.title,
         });
      }
   };
}

initializeChakraComponent('ProductAdCards', ProductAdCards);
